import React from 'react';
import {
    Button,
    FormGroup,
    Progress,
    Alert,
    UncontrolledButtonDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
} from 'reactstrap';
import * as moment from 'moment';
import { isEmpty } from 'lodash-es';
import {
    faMoneyCheck, faCalculator, faDownload, faUndo, faArrowUpRightFromSquare,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from '@s20/components/common/Link';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes, ApplicationPermissions, AppNavPaths } from '../Common';
import { Can } from '../Can';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    IconCellRenderer,
    LinkCellRenderer,
    TextFilterDefaults,
    DateFilterDefaults,
    VariableLinkCellRenderer,
    FlagIconCellRenderer,
} from '../common/dataGrid/DataGrid';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import {
    onFieldChange,
    PageHeading,
    PageWrap,
    toasty,
    SmallButton,
    Input,
    Textarea,
    FlexBetweenRow,
    FormCheckbox,
    FlexColumnStart,
    FormLabel,
} from '../common/forms/FormElements';
import TimesheetDetails from '../timesheet/TimesheetDetails';
import { NotAuthorizedPage } from '../status/StatusCodes';
import PayrollCalculationSlideout from '../payrollManagement/PayrollCalculationSlideout';
import { PayrollAppTypes, PayrollAppTypeOptions } from '../tenant/Tenant';
import { PayPeriodTypes } from '../organization/Organization';
import { DatePicker } from '../common/forms/DatePicker';
import { Modal } from '../common/Modal';
import Chip from '../common/forms/Chip';
import { PayrollExportCategories, PayrollFlags, TimesheetEmergencyPayTypes } from './Payroll';

const DEFAULT_SEARCH_WINDOW_DAYS = 14;

// The grid opens on, and falls back to, this window. Kept in one place so the initial
// load and the clear-filter guard can never drift apart.
const applyDefaultJobDateRange = (api) => {
    const filterInstance = api.getFilterInstance('Payroll.Timesheet.JobDate');
    if (!filterInstance) {
        return;
    }

    filterInstance.setModel({
        type: 'inRange',
        dateFrom: moment()
            .subtract(DEFAULT_SEARCH_WINDOW_DAYS, 'days')
            .format('YYYY-MM-DD'),
        dateTo: moment().subtract(1, 'days').format('YYYY-MM-DD'),
    });
};

const getExcelAPIPath = (hasFull) => {
    const url = hasFull
        ? ApiRoutes.payroll.excelExport()
        : ApiRoutes.payroll.excelExportIndividual();
    return url;
};

const getPayrollAppLabel = (payrollAppTypeId) => PayrollAppTypeOptions.find((o) => payrollAppTypeId != 1 && o.value == payrollAppTypeId)?.label ?? 'Payroll';

const printTimesheet = (id, hasFull) => {
    if (id) {
        const url = hasFull
            ? ApiRoutes.report.timesheetPayroll(id)
            : ApiRoutes.report.timesheetPayrollIndividual(id);

        httpClient.open(url, '_self');
    }
};

const transformRowData = (data) => {
    data.rows.forEach((row) => {
        // Combine these columns to save space.
        row.quantityDisplay = `${row.quantity} ${row.unitsName}`;

        // Placeholder column so we can have it be the last checkable thing on the right.
        // Otherwise, we could set checkboxSelection = true for an existing column.
        row.selectionPlaceholder = '';
    });
    return { ...data };
};

const onSelectedPayrollWeekChanged = async (date, selectedDispatchLocation, setFieldValue) => {
    const model = {
        weekOf: date,
        dispatchLocations: (selectedDispatchLocation || []).map((item) => item.id),
    };

    let employees = [];
    try {
        const entities = await httpClient.post(
            ApiRoutes.payroll.employees(),
            model,
        );
        if (entities) {
            employees = entities.map((e) => ({
                ...e,
                value: e.id,
                label: e.name,
            }));
        }
    } catch (error) {
        console.log(error);
    }

    setFieldValue('employees', employees);
};

const onSelectedPayrollDispatchLocationChanged = async (selection, selectedWeekOf, setFieldValue) => {
    setFieldValue('loadingEmployees', true);

    const model = {
        weekOf: new Date(selectedWeekOf),
        dispatchLocations: selection.map((item) => item.id),
    };

    let employees = [];
    try {
        const entities = await httpClient.post(
            ApiRoutes.payroll.employees(),
            model,
        );
        employees = entities.map((e) => ({
            ...e,
            value: e.id,
            label: e.name,
        }));
    } catch (error) {
        console.log(error);
    }

    setFieldValue('employees', employees);
    setFieldValue('loadingEmployees', false);
};

class PayrollIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.timesheetDetailsRef = React.createRef();
        this.payrollCalculationRef = React.createRef();

        this.state = {
            loading: true,
            rowData: [],
            rowsSelected: [],
            selectedPayrollCompany: null,
            selectedDispatchLocation: null,
            selectedMultiDispatchLocations: null,
            selectedWeekOf: '',
            selectedMonthOf: '',
            exportCategory: 1,
            selectEmployee: 0,
            showExportModal: false,
            showCertifiedPayrollModal: false,
            isPreviewExport: false,
            isExporting: false,
            isDownloading: false,
            isRecalculating: false,
            showDownloadModal: false,
            showRecalcModal: false,
            selectedExportId: null,
            selectedRowRejectionNotes: '',
            isStandardExport: false,
            contracts: [],
        };

        this.onRowClicked = this.onRowClicked.bind(this);
        this.onRowSelected = this.onRowSelected.bind(this);
        this.onChange = this.onChange.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    showBlockedTimesheetsModal(blockedMessage, blockedTimesheets) {
        Modal.show({
            title: 'Payroll Processing Blocked',
            SaveButtonProps: {
                hidden: true,
            },
            onClose: () => this.setState({ isRecalculating: false }),
            body: () => (
                <div className="mb-4">
                    <div
                        style={{ marginBottom: '20px' }}
                        className="form-text text-danger"
                    >
                        <strong>{blockedMessage}</strong>
                    </div>
                    {blockedTimesheets.map((ts) => (
                        <Chip
                            key={`${ts.id}`}
                            component={Link}
                            to={`${AppNavPaths.TimesheetManagement}?t=${ts.id}`}
                            target="_blank"
                            className="mr-2"
                            text={(
                                <span>
                                    {ts.timesheetNumber}
                                    <FontAwesomeIcon
                                        icon={faArrowUpRightFromSquare}
                                        className="ms-3"
                                    />
                                </span>
                            )}
                        />
                    ))}
                </div>
            ),
        });
    }

    async onAcceptButtonClick() {
        const { rowsSelected } = this.state;

        // call accept
        if (rowsSelected) {
            const model = rowsSelected.map((x) => x.id);
            const response = await httpClient.post(
                ApiRoutes.payroll.approve(),
                model,
            );

            if (response) {
                toasty.error(`Error approving payroll records: ${response}`);
            } else {
                toasty.success('Payroll records approved');
            }
        }

        this.state.gridOptions.refresh();

        this.setState({ rowsSelected: [] });
    }

    onChange = onFieldChange;

    onDownloadExportClick = async (selectedExportId) => {
        if (selectedExportId) {
            /* var model = { id: id }; */
            await httpClient
                .downloadFile(
                    ApiRoutes.payroll.download(selectedExportId),
                    null,
                    'Export',
                )
                .catch(this.handleSaveError);
        }
    };

    async onHourlyExportClick(values) {
        const { exporting } = this.state;

        if (!exporting) {
            const { selectedWeekOf } = values;

            if (!selectedWeekOf) return;

            this.setState({ isExporting: true });

            const model = {
                weekOf: new Date(selectedWeekOf),
            };

            try {
                const fileName = `HourlyReport_${moment().format(
                    'YYYY-M-D_hhmmss',
                )}.xlsx`;

                await httpClient.downloadFile(
                    ApiRoutes.payroll.excelHoursExport(),
                    model,
                    fileName,
                );
            } catch (error) {
                toasty.error('There was a problem getting the hourly export.');
                console.error({ error });
            } finally {
                this.setState({
                    isExporting: false,
                    showExportModal: false,
                });
            }
        }
    }

    onPayrollCalculationClosed = async () => {
        await this.context.setFormOpened(false);
        this.setState({ selectedRow: null });
        this.state.gridOptions.refresh();
    };

    async onRecalcClick(values) {
        const { selectedWeekOf, selectedDispatchLocation, selectedEmployeeIds } = values;

        const model = {
            selectedEmployeeIds,
            weekOf: new Date(selectedWeekOf),
            dispatchLocations: selectedDispatchLocation.map((item) => item.id),
        };

        // Check to see if this employee has payroll edits
        // If so warn the user of overwrite
        try {
            const hasEdits = await httpClient.post(
                ApiRoutes.payroll.doesPayrollHaveEdits(),
                model,
            );
            if (hasEdits) {
                const confirmed = window.confirm(
                    'Some of these charges have edits.  Continue and overwrite the edits?',
                );
                if (!confirmed) return;
            }

            const hasExports = await httpClient.post(
                ApiRoutes.payroll.doesPayrollHaveExports(),
                model,
            );
            if (hasExports) {
                window.alert(
                    'Employees with exported charges cannot be recalculated.',
                );
                return;
            }
        } catch (error) {
            toasty.error('An error occurred while preparing to recalculate.');
            console.error({ error });
            return;
        }

        // Recalculate
        this.setState({ isRecalculating: true });
        try {
            const response = await httpClient.post(
                ApiRoutes.payrollManagement.recalculateWeek(),
                model,
            );
            if (response) {
                if (response.data?.length > 0) {
                    this.showBlockedTimesheetsModal(response.message, response.data);
                    return { keepOpen: true };
                }
                if (response.message) {
                    toasty.error(response.message);
                    this.state.gridOptions.refresh();
                    this.setState({ rowsSelected: [], isRecalculating: false });
                } else {
                    toasty.success('Employee\'s week recalculated successfully.');
                    this.state.gridOptions.refresh();
                    this.setState({ rowsSelected: [], isRecalculating: false });
                }
            }
        } catch (error) {
            toasty.error('An error occurred recalculating.');
            console.error({ error });
        } finally {
            this.setState({ isRecalculating: false });
        }
    }

    onRowClicked = (event) => {
        const selection = event.api.getSelectedRows();
        const row = selection.length ? selection[0] : null;

        if (row) {
            this.setState({ selectedRow: row });
        }
    };

    // https://stackoverflow.com/questions/44263350/count-number-of-selected-rows-in-ag-grid
    // Possibly use lodash dequeue?
    onRowSelected(e) {
        const rs = e.api.getSelectedRows();

        this.setState({
            rowsSelected: rs,
        });
    }

    onSelectedDispatchLocationChanged = (selection) => {
        const loc = { ...selection };
        this.setState({ selectedDispatchLocation: loc.value });
    };

    onSelectedMultiDispatchLocationChanged = (opts) => {
        this.setState({ selectedMultiDispatchLocations: opts });
    };

    onSelectedPayrollCompanyChanged = (selection) => {
        const loc = { ...selection };
        this.setState({ selectedPayrollCompany: loc.value });

        const subArr = this.state.dispatchLocations.filter(
            (x) => x.parentCompanyId == loc.value,
        );
        this.setState({ selectedMultiDispatchLocations: subArr });
    };

    onTimesheetDetailsClosed = async () => {
        await this.context.setFormOpened(false);
        this.setState({ selectedRow: null });
    };

    onPayrollExportCategoryChanged = (selection) => {
        this.setState({ exportCategory: selection.value });
    };

    async onUnionExportClick(values) {
        const { exporting } = this.state;

        if (!exporting) {
            const {
                selectedWeekOf,
                selectedMultiDispatchLocations,
                exportCategory,
            } = values;

            if (!selectedWeekOf || !selectedMultiDispatchLocations) return;

            this.setState({ isExporting: true, exporting: true });

            const model = {
                weekOf: new Date(selectedWeekOf),
                dispatchLocationIds: selectedMultiDispatchLocations.map(
                    (e) => e.id),
                previewExport: false,
                exportCategory,
            };

            try {
                let identifier = '';

                if (exportCategory === 2) {
                    identifier = 'PW';
                } else if (exportCategory === 3) {
                    identifier = 'SP';
                }

                const { tenantSettings } = this.context.tenant;
                const fileExt = tenantSettings.payrollAppTypeId === PayrollAppTypes.ADP ? 'csv' : 'xlsx';
                const fileName = `PayrollExport${identifier}_${moment().format(
                    'YYYY-M-D_hhmmss',
                )}.${fileExt}`;

                await httpClient.downloadFile(
                    ApiRoutes.payroll.unionExport(),
                    model,
                    fileName,
                );

                this.state.gridOptions.refresh();
            } catch (error) {
                toasty.error('There was a problem downloading the union payroll export.');
                console.error({ error });
            } finally {
                this.setState({
                    exporting: false,
                    isExporting: false,
                    showExportModal: false,
                    isPreviewExport: false,
                    exportCategory: 1,
                });
            }
        }
    }

    async onPreviewExportClick(values, legacyExport) {
        const { selectedWeekOf, selectedMultiDispatchLocations, exportCategory } = values;
        if (!selectedWeekOf || !selectedMultiDispatchLocations) return;

        this.setState({ isExporting: true, exporting: true });

        const model = {
            exportCategory,
            weekOf: new Date(selectedWeekOf),
            dispatchLocationIds: selectedMultiDispatchLocations.map((e) => e.id),
            previewExport: true,
            legacyExport,
        };

        try {
            let identifier = '';

            if (exportCategory === 2) {
                identifier = 'PW';
            } else if (exportCategory === 3) {
                identifier = 'SP';
            }

            const { tenantSettings } = this.context.tenant;
            const fileExt = !legacyExport && tenantSettings.payrollAppTypeId === PayrollAppTypes.ADP ? 'csv' : 'xlsx';
            const fileName = `Preview_PayrollExport${identifier}_${moment().format('YYYY-M-D_hhmmss')}.${fileExt}`;
            await httpClient.downloadFile(
                ApiRoutes.payroll.unionExport(),
                model,
                fileName,
            );
            this.state.gridOptions.refresh();
        } catch (error) {
            toasty.error('There was a problem downloading the preview payroll export.');
            console.error({ error });
        } finally {
            this.setState({ exporting: false, isExporting: false, showExportModal: false });
        }
    }

    async onWeeklyExportClick(values, exportBillableOnly) {
        const { exporting } = this.state;

        if (!exporting) {
            const {
                exportCategory,
                selectedWeekOf,
                selectedDispatchLocation,
            } = values;

            if (!selectedWeekOf || !selectedDispatchLocation) return;

            this.setState({ isExporting: true });

            const dispatchLocationIds = [];
            dispatchLocationIds.push(selectedDispatchLocation);
            const model = {
                weekOf: new Date(selectedWeekOf),
                dispatchLocationIds,
                exportBillableOnly,
                exportCategory,
            };

            try {
                let fileName = `PayrollWeeklyExport_${moment().format(
                    'YYYY-M-D_hhmmss',
                )}.xlsx`;

                if (exportBillableOnly) {
                    fileName = `BillingWeeklyExport_${moment().format(
                        'YYYY-M-D_hhmmss',
                    )}.xlsx`;
                } else if (exportCategory === 2) {
                    fileName = `PayrollWeeklyExportPW_${moment().format(
                        'YYYY-M-D_hhmmss',
                    )}.xlsx`;
                } else if (exportCategory === 3) {
                    fileName = `PayrollWeeklyExportSP_${moment().format(
                        'YYYY-M-D_hhmmss',
                    )}.xlsx`;
                }

                await httpClient.downloadFile(
                    ApiRoutes.payroll.excelWeeklyExport(),
                    model,
                    fileName,
                );
            } catch (error) {
                toasty.error('There was a problem getting the weekly export.');
                console.error({ error });
            } finally {
                this.setState({
                    isExporting: false,
                    showExportModal: false,
                    exportCategory: 1,
                });
            }
        }
    }

    getColumnDefs(hasFullView, hasEdit, hasIndividual) {
        const { tenantSettings } = this.context.tenant;

        const dispatchFilterParams = {
            suppressFilterButton: true,
            labelText: 'Filter by Dispatch',
            options: this.state.dispatchLocations,
            optionsLabel: 'label',
            optionsValue: 'value',
        };
        const payrollStatusFilterParams = {
            suppressFilterButton: true,
            options: this.state.payrollStatuses,
            optionsLabel: 'label',
            optionsValue: 'value',
        };
        const billingStatusFilterParams = {
            suppressFilterButton: true,
            options: this.state.billingStatuses,
            optionsLabel: 'label',
            optionsValue: 'value',
        };
        const flagsFilterParams = {
            suppressFilterButton: true,
            options: this.state.flags,
            optionsLabel: 'label',
            optionsValue: 'value',
        };
        const yesNoFilterParams = {
            suppressFilterButton: true,
            options: this.state.yesNo,
            optionsLabel: 'label',
            optionsValue: 'value',
        };

        const defs = [
            {
                headerName: '',
                valueGetter: 'node.id',
                sortable: false,
                hide: true,
                flex: 1,
                maxWidth: 35,
                minWidth: 35,
                cellRenderer: this.indexCellRenderer,
            },
            {
                colId: 'Payroll.Timesheet.JobDate',
                headerName: 'Date',
                field: 'date',
                sortable: true,
                minWidth: 120,
                sort: { direction: 'asc', priority: 0 },
                filter: 'agDateColumnFilter',
                filterParams: DateFilterDefaults,
            },
            {
                colId: 'Start',
                headerName: 'Start',
                field: 'start',
                sortable: false,
                maxWidth: 140,
                minWidth: 140,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'End',
                headerName: 'End',
                field: 'end',
                sortable: false,
                maxWidth: 140,
                minWidth: 140,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Payroll.Employee.FullName',
                headerName: 'Emp. Name',
                field: 'employeeName',
                sortable: true,
                flex: 1.5,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                cellStyle: (q) => {
                    if ((q.data ?? {}).onBoarding) {
                        if (q.data.onBoarding) {
                            return { color: 'red', fontWeight: 'bold' };
                        }
                    }

                    return {};
                },
            },
            {
                colId: 'Payroll.Employee.DispatchCompany.CompanyName',
                headerName: 'Dispatching',
                sortable: true,
                flex: 1.5,
                hide: hasIndividual,
                field: 'dispatchLocation',
                tooltipField: 'dispatchOfficeName',
                filter: 'selectFilter',
                filterParams: dispatchFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: dispatchFilterParams,
            },
            {
                colId: 'Payroll.CustomerCompanyName',
                headerName: 'Customer Name',
                field: 'customerName',
                sortable: true,
                flex: 1.5,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                valueGetter: (row) => {
                    if (row.data) {
                        const name = `${row.data.customerName}${row.data.subcontractorName
                            ? ` - ${row.data.subcontractorName}`
                            : ''
                            }`;
                        return name;
                    }

                    return '';
                },
            },
            {
                colId: 'Payroll.ChargeTypeName',
                headerName: 'Charge Type',
                field: 'chargeType',
                sortable: true,
                flex: 1,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Payroll.Timesheet.TimesheetNumber',
                headerName: 'Timesheet #',
                field: 'timesheetNumber',
                sortable: true,
                flex: 1,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Payroll.TimesheetDetail.ChargeTypeIsBillable',
                headerName: 'Billable',
                field: 'billable',
                sortable: false,
                maxWidth: 140,
                minWidth: 140,
                hide: hasIndividual,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: yesNoFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: yesNoFilterParams,
            },
            {
                colId: 'Charge',
                headerName: 'Charge',
                field: 'charge',
                type: 'rightAligned',
                sortable: false,
                maxWidth: 120,
                minWidth: 120,
                hide: hasIndividual,
                cellStyle: (q) => {
                    if ((q.data ?? {}).isBreak) {
                        if (q.data.isPaidBreak) {
                            return { color: 'blue' };
                        }
                        return { color: 'red' };
                    }

                    return {}; // dont return '' will break grid
                },
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Pay',
                headerName: 'Pay',
                field: 'pay',
                sortable: false,
                maxWidth: 100,
                minWidth: 100,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'REG',
                headerName: 'REG',
                field: 'reg',
                sortable: false,
                maxWidth: 100,
                minWidth: 100,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'RSD',
                headerName: 'RSD',
                field: 'rsd',
                sortable: false,
                maxWidth: 100,
                minWidth: 100,
                hide: !tenantSettings.shiftDifferentialPayAdjustment && !tenantSettings.enableRegularShiftDifferential && !tenantSettings.enforceContractUnionWork,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'OT',
                headerName: 'OT',
                field: 'ot',
                sortable: false,
                maxWidth: 100,
                minWidth: 100,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'OTSD',
                headerName: 'OTSD',
                field: 'otsd',
                sortable: false,
                maxWidth: 100,
                minWidth: 100,
                hide: !tenantSettings.shiftDifferentialPayAdjustment && !tenantSettings.enableRegularShiftDifferential && !tenantSettings.enforceContractUnionWork,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'T15',
                headerName: 'T15',
                field: 't15',
                sortable: false,
                maxWidth: 100,
                minWidth: 100,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'T15SD',
                headerName: 'T15SD',
                field: 't15SD',
                sortable: false,
                maxWidth: 100,
                minWidth: 100,
                hide: !tenantSettings.shiftDifferentialPayAdjustment && !tenantSettings.enableRegularShiftDifferential && !tenantSettings.enforceContractUnionWork,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'EMG',
                headerName: 'EMG',
                field: 'emg',
                sortable: false,
                maxWidth: 100,
                minWidth: 100,
                hide: tenantSettings.quirksEnabled,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'ESD',
                headerName: 'ESD',
                field: 'esd',
                sortable: false,
                maxWidth: 100,
                minWidth: 100,
                hide: tenantSettings.quirksEnabled
                    || (!tenantSettings.shiftDifferentialPayAdjustment && !tenantSettings.enableRegularShiftDifferential && !tenantSettings.enforceContractUnionWork),
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'EOT',
                headerName: 'EOT',
                field: 'eot',
                sortable: false,
                maxWidth: 100,
                minWidth: 100,
                hide: tenantSettings.quirksEnabled
                    || tenantSettings.timesheetEmergencyPayTypeId === TimesheetEmergencyPayTypes.TimeAndAHalf,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'EOTSD',
                headerName: 'EOTSD',
                field: 'eotsd',
                sortable: false,
                maxWidth: 100,
                minWidth: 100,
                hide: tenantSettings.quirksEnabled
                    || (!tenantSettings.shiftDifferentialPayAdjustment && !tenantSettings.enableRegularShiftDifferential && !tenantSettings.enforceContractUnionWork)
                    || tenantSettings.timesheetEmergencyPayTypeId === TimesheetEmergencyPayTypes.TimeAndAHalf,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'PR',
                headerName: 'PR',
                field: 'pr',
                sortable: false,
                maxWidth: 100,
                minWidth: 100,
                hide: !tenantSettings.enableAlternativePayrollBilling,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'PRSD',
                headerName: 'PRSD',
                field: 'prsd',
                sortable: false,
                maxWidth: 100,
                minWidth: 100,
                hide: !tenantSettings.enableAlternativePayrollBilling
                    || (!tenantSettings.shiftDifferentialPayAdjustment && !tenantSettings.enableRegularShiftDifferential && !tenantSettings.enforceContractUnionWork),
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'DT',
                headerName: 'DT',
                field: 'dt',
                sortable: false,
                maxWidth: 100,
                minWidth: 100,
                hide: !tenantSettings.enableDoubleTimePayroll,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'DTSD',
                headerName: 'DTSD',
                field: 'dtsd',
                sortable: false,
                maxWidth: 100,
                minWidth: 100,
                hide: !tenantSettings.enableDoubleTimePayroll
                    || (!tenantSettings.shiftDifferentialPayAdjustment && !tenantSettings.enableRegularShiftDifferential && !tenantSettings.enforceContractUnionWork),
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'PW',
                headerName: 'PW',
                field: 'pw',
                sortable: false,
                maxWidth: 100,
                minWidth: 100,
                hide: !tenantSettings.enablePrevailingWageContracts,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'PWOT',
                headerName: 'PWOT',
                field: 'pwot',
                sortable: false,
                maxWidth: 100,
                minWidth: 100,
                hide: !tenantSettings.enablePrevailingWageContracts,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'PWT15',
                headerName: 'PWT15',
                field: 'pwT15',
                sortable: false,
                maxWidth: 100,
                minWidth: 100,
                hide: !tenantSettings.enablePrevailingWageContracts,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            // Users without full access can only access Exported records (although they can only see Print view records.)
            {
                colId: 'Payroll.PayrollStatus.Description',
                headerName: 'Status',
                field: 'status',
                sortable: true,
                maxWidth: 140,
                minWidth: 140,
                hide: !hasEdit,
                cellRenderer: 'variableLinkRenderer',
                cellRendererParams: (r) => {
                    if ((r.data ?? {}).payrollExportId) {
                        return {
                            clicked: this.toggleDownloadModal,
                            isLink: true,
                            nameField: 'status',
                            idField: 'payrollExportId',
                            title: 'Download Export',
                        };
                    }
                    return {
                        clicked: this.toggleDownloadModal,
                        isLink: false,
                        nameField: 'status',
                        idField: 'payrollExportId',
                        title: 'Download Export',
                    };
                },
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: payrollStatusFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams:
                    payrollStatusFilterParams,
            },
            {
                colId: 'Payroll.PayrollExportId',
                headerName: 'Export Id',
                field: 'payrollExportId',
                sortable: true,
                maxWidth: 100,
                minWidth: 100,
                hide: !hasEdit,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Payroll.Timesheet.BillingStatus.Description',
                headerName: 'Billing Status',
                field: 'billingStatus',
                sortable: true,
                flex: 1.5,
                hide: !hasEdit,
                tooltipField: 'status',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: billingStatusFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: billingStatusFilterParams,
            },
            {
                colId: 'Payroll.WorkOrderNumber',
                headerName: 'Work Order',
                field: 'workOrderNumber',
                sortable: false,
                flex: 1,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Flag',
                headerName: 'Flag',
                field: 'flag',
                flex: 1,
                minWidth: 65,
                sortable: false,
                tooltipField: 'flag',
                tooltipComponentParams: { color: '#ececec' },
                cellRenderer: 'flagIconRenderer',
                cellRendererParams: {
                    bodyField: 'flag',
                },
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: flagsFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: flagsFilterParams,
            },
            {
                colId: 'Id',
                flex: 0,
                minWidth: 75,
                maxWidth: 75,
                hide: !hasEdit,
                headerName: 'View',
                sortable: false,
                cellRenderer: 'iconRenderer',
                cellRendererParams: {
                    clicked: this.openTimesheetDetails,
                    idField: 'timesheetId',
                    iconClass: 'fa-eye',
                    hidden: (data) => !!data?.payrollChargeTypeId && !data?.prd,
                },
            },
            {
                colId: 'Id',
                minWidth: 75,
                maxWidth: 75,
                headerName: 'Print',
                sortable: false,
                cellRenderer: 'iconRenderer',
                cellRendererParams: {
                    clicked: (id) => printTimesheet(id, hasFullView),
                    idField: 'timesheetId',
                    iconClass: 'fa-print',
                    hidden: (data) => !!data?.payrollChargeTypeId && !data?.prd,
                },
            },
            {
                colId: 'Id',
                minWidth: 75,
                maxWidth: 75,
                hide: !hasEdit,
                headerName: '',
                sortable: false,
                cellRenderer: 'iconRenderer',
                cellRendererParams: {
                    clicked: this.openPayrollCalculation,
                    idField: 'id',
                    iconClass: 'fa-edit payroll-edit-btn',
                },
            },
            {
                colId: 'SelectionPlaceholder',
                minWidth: 75,
                hide: !hasEdit,
                headerName: '',
                sortable: false,
                checkboxSelection(params) {
                    if (params.data) {
                        return params.data.status !== 'Exported';
                    }

                    return null;
                },
            },
        ];

        return defs;
    }

    openPayrollCalculation = async (id) => {
        if (id) {
            await this.context.setFormOpened(true);
            this.payrollCalculationRef.current.open(id);
        }
    };

    openTimesheetDetails = async (id) => {
        if (id) {
            await this.context.setFormOpened(true);
            this.timesheetDetailsRef.current.open(id);
        }
    };

    populateState = async () => {
        const payrollStatusesPromise = httpClient.js(
            ApiRoutes.typeAheads.payrollStatuses(),
        );
        const billingStatusesPromise = httpClient.js(
            ApiRoutes.typeAheads.billingStatuses(),
        );
        const payrollCompaniesPromise = httpClient.js(
            ApiRoutes.typeAheads.payrollCompanies(),
        );
        const payrollFlagsPromise = httpClient.js(
            ApiRoutes.typeAheads.payrollCategoryFlags(),
        );

        const [
            payrollStatuses,
            billingStatuses,
            payrollCompanies,
            flags,
        ] = await Promise.all([
            payrollStatusesPromise,
            billingStatusesPromise,
            payrollCompaniesPromise,
            payrollFlagsPromise,
        ]);

        const yesNo = [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' },
        ];

        // 2022-10-10 - M. Nicol
        // If the user has Payroll View, (s)he can see all data on the page.
        // See permission setup in App.js.
        // Edit allows use of red Export button.
        // If the user is neither of the below, (s)he must have payroll.viewindividual.
        const canViewBillingReport = !!this.context.checkUserHasPermission(ApplicationPermissions.billing_report_view);
        let hasFullView = !!this.context.checkUserHasPermission(ApplicationPermissions.payroll_view);
        let hasIndividual = !!this.context.checkUserHasPermission(ApplicationPermissions.payroll_view_individual);
        let hasEdit = !!this.context.checkUserHasPermission(ApplicationPermissions.payroll_edit);

        if (this.context?.user?.isAdmin) {
            hasIndividual = false;
            hasEdit = true;
            hasFullView = true;
        }

        let dispatchLocations = await httpClient.js(
            ApiRoutes.company.dispatchCompanies(),
        );
        dispatchLocations = dispatchLocations.map((e) => ({
            ...e,
            value: e.id,
            label: e.companyName,
        }));

        const contracts = await httpClient.js(
            ApiRoutes.typeAheads.contracts(),
        );

        const self = this;
        const employees = [];

        this.setState({
            employees,
            dispatchLocations,
            payrollStatuses,
            billingStatuses,
            flags,
            yesNo,
            payrollCompanies,
            hasFullView,
            hasEdit,
            hasIndividual,
            contracts,
        }, () => {
            const gridOptions = createGridOptions(self);

            gridOptions.components = {
                selectFilter: DataGridSelectFilter,
                selectFloatingFilter: DataGridSelectFloatingFilter,
                nameRenderer: LinkCellRenderer,
                iconRenderer: IconCellRenderer,
                flagIconRenderer: FlagIconCellRenderer,
                variableLinkRenderer: VariableLinkCellRenderer,
            };

            gridOptions.rowClassRules = {
                'ag-row-danger': (params) => (params.data ?? {}).isEmergency === true,
                'ag-row-success': (params) => {
                    const matches = ((params.data ?? {}).flag ?? []).filter((f) => (
                        f === PayrollFlags.ChargeTypeThresholdExceeded
                        || f === PayrollFlags.BreakThresholdExceeded
                    ));

                    return matches.length > 0;
                },
            };

            // https://www.ag-grid.com/documentation/javascript/row-selection/#checkbox-selection
            // One column must have "checkboxSelection" set to true.
            // Note: headerCheckboxSelection not available with infinite scroll.
            gridOptions.rowSelection = 'multiple';

            gridOptions.postProcessData = transformRowData;
            gridOptions.onRowSelected = self.onRowSelected;
            gridOptions.columnDefs = self.getColumnDefs(
                hasFullView,
                hasEdit,
                hasIndividual,
            );

            // On the grid's first load, we want to apply the filters immediately from the state population,
            // before the API call is made.
            gridOptions.onGridReady = (params) => {
                applyDefaultJobDateRange(gridOptions.api);

                gridOptions.api.onFilterChanged();

                params.api.setDatasource(dataSource); // eslint-disable-line
                self.setState({ gridApi: params.api });
            };

            // This screen searches Processed and Exported payroll, which covers everything that has
            // ever run, so the date range is the only filter that narrows it. Clearing it leaves the
            // database sorting the whole table to return one page, so put the default window back.
            gridOptions.onFilterChanged = (params) => {
                const dateFilter = params.api.getFilterInstance(
                    'Payroll.Timesheet.JobDate',
                );

                // Re-entrant: setting the model below fires this again, and by then there is a
                // model, so it returns here rather than looping.
                if (!dateFilter || dateFilter.getModel()) {
                    return;
                }

                applyDefaultJobDateRange(params.api);
                params.api.onFilterChanged();
                toasty.info(
                    `Showing the last ${DEFAULT_SEARCH_WINDOW_DAYS} days. Pick a date range to search further back.`,
                );
            };

            // 2022-09-28 - M. Nicol - If the user does not have full access, (s)he can only see his/her own data.
            const searchUrl = ApiRoutes.payroll.search();

            if (hasIndividual) {
                searchUrl = ApiRoutes.payroll.searchindividual();
            }

            const dataSource = createDataSource(searchUrl, gridOptions);

            this.setState({
                loading: false,
                gridOptions,
                dataSource,
                canViewBillingReport,
                // selectedMultiDispatchLocations: dispatchLocations
            });
        });
    };

    rejectPayrollTimesheet = async ({ selectedRowRejectionNotes }) => {
        const { rowsSelected } = this.state;

        if (
            selectedRowRejectionNotes.length === 0
            || !selectedRowRejectionNotes.trim()
        ) return;

        // call reject
        if (rowsSelected) {
            const model = {
                payrollId: rowsSelected[0].id,
                note: selectedRowRejectionNotes,
            };

            const endPoint = ApiRoutes.payroll.payrollReject();
            const response = await httpClient
                .post(endPoint, model)
                .catch(this.handleSaveError);

            if (response) {
                toasty.success('Charges rejected');
            }
        }

        this.state.gridOptions.refresh();

        this.setState({
            selectedRow: null,
            rowsSelected: [],
        });
    };

    toggleDownloadModal = (id) => {
        Modal.show({
            title: 'Download Payroll',
            onSubmit: () => this.onDownloadExportClick(id),
            SaveButtonProps: {
                color: 'success',
                icon: faDownload,
                label: 'Download',
                savingLabel: 'Downloading',
            },
            body: () => (
                <>
                    Click the Download button to download payroll export
                    {' '}
                    <strong>{id}</strong>
                </>
            ),
        });
    };

    toggleExportModal(isWeekly, exportBillableOnly, isHourly, isStandardExport, isPreviewExport, isLegacyExport) {
        const {
            payrollCompanies, dispatchLocations,
        } = this.state;

        const { tenant } = this.context;
        const { tenantSettings } = tenant;
        const { payPeriodTypeId, enablePrevailingWageContracts, quirksEnabled } = tenantSettings;

        const previousWeekStart = moment()
            .startOf('isoWeek')
            .subtract(1, 'week')
            .toDate();

        const options = {
            isPreviewExport: !!isPreviewExport,
            isStandardExport: !!isStandardExport,
            isWeekly: !!isWeekly,
            exportBillableOnly: !!exportBillableOnly,
            isHourly: !!isHourly,
        };

        Modal.show({
            title: this.getExportModalHeader(options),
            onSubmit: async (values) => {
                if (isPreviewExport) {
                    await this.onPreviewExportClick(values, isLegacyExport == true);
                } else if (isStandardExport) {
                    await this.onUnionExportClick(values);
                } else if (isWeekly) {
                    await this.onWeeklyExportClick(values, exportBillableOnly);
                } else if (isHourly) {
                    await this.onHourlyExportClick(values);
                } else {
                    await this.onUnionExportClick(values);
                }
            },
            SaveButtonProps: {
                label: 'Export',
                savingLabel: 'Exporting',
                icon: faDownload,
            },
            initialValues: {
                selectedWeekOf: previousWeekStart,
                exportCategory: 1,
                selectedPayrollCompany: '',
                selectedDispatchLocation: '',
                selectedMultiDispatchLocations: '',
            },
            body: ({ setFieldValue, values }) => {
                let selectableDispatchCompanies = dispatchLocations;

                if (values.selectedPayrollCompany) {
                    selectableDispatchCompanies = dispatchLocations.filter(
                        (x) => x.parentCompanyId === values.selectedPayrollCompany,
                    );
                }

                return (
                    <>
                        {Boolean(isStandardExport) && !isPreviewExport && (
                            <Alert color="warning">
                                Note that any payroll records that are exported in this way will have their status changed to
                                {' '}
                                <strong>Exported</strong>
                                .
                            </Alert>
                        )}

                        {!isWeekly && !isHourly && quirksEnabled && (
                            <ValidatedSelect
                                placeholder="Select Operating Company"
                                id="selectedPayrollCompany"
                                name="selectedPayrollCompany"
                                label="Operating Company"
                                isClearable={false}
                                options={payrollCompanies}
                                required={!isPreviewExport}
                                onChange={(e) => {
                                    if (e?.value) {
                                        const companies = dispatchLocations.filter(
                                            (x) => x.parentCompanyId === e.value,
                                        );

                                        setFieldValue('selectedMultiDispatchLocations', companies);
                                    }
                                }}
                            />
                        )}

                        {!isWeekly && !isHourly && (
                            <ValidatedSelect
                                placeholder="Select Dispatching"
                                id="selectedMultiDispatchLocations"
                                name="selectedMultiDispatchLocations"
                                label="Dispatching"
                                isClearable
                                isMulti
                                options={selectableDispatchCompanies}
                                required
                                error="You must select a dispatch location"
                                onSelectAll={() => {
                                    setFieldValue('selectedMultiDispatchLocations', selectableDispatchCompanies);
                                }}
                            />
                        )}

                        {!!isWeekly && (
                            <ValidatedSelect
                                placeholder="Select Dispatching"
                                id="selectedDispatchLocation"
                                name="selectedDispatchLocation"
                                label="Dispatching"
                                isClearable={false}
                                options={selectableDispatchCompanies}
                                required
                                error="You must select a dispatch location"
                            />
                        )}

                        <DatePicker
                            id="selectedWeekOf"
                            label={payPeriodTypeId === PayPeriodTypes.Weekly || exportBillableOnly || isHourly ? 'Week Of' : 'Period Of'}
                            name="selectedWeekOf"
                            highlightDates={this.getHighlightedPayPeriodDates(options)}
                            required
                            error="You must select a pay period"
                        />

                        {Boolean(enablePrevailingWageContracts) && (isWeekly || isStandardExport) && !exportBillableOnly && (
                            <ValidatedSelect
                                id="exportCategory"
                                name="exportCategory"
                                label="Export Category"
                                isClearable={false}
                                options={PayrollExportCategories}
                                error="You must select an export category"
                                required
                            />
                        )}
                    </>
                );
            },
        });
    }

    getExportModalHeader = ({
        isPreviewExport, isStandardExport, isWeekly, exportBillableOnly, isHourly,
    }) => {
        const { payrollAppTypeId } = this.context.tenant.tenantSettings;

        if (isPreviewExport) {
            return 'Download Preview Export';
        }

        if (isStandardExport) {
            return `Export to ${getPayrollAppLabel(payrollAppTypeId)}`;
        }

        if (!isWeekly && !exportBillableOnly && !isHourly) {
            return 'Export Payroll';
        }

        if (isHourly) {
            return 'Hours Report';
        }

        if (isWeekly && !exportBillableOnly) {
            return 'Export Payroll - All Time';
        }

        return 'Export Payroll - Billing';
    };

    toggleCertifiedPayrollModal = () => {
        const { contracts } = this.state;

        Modal.show({
            title: 'Export Certified Payroll',
            onSubmit: (values) => this.onCertifiedPayrollExportClicked(values),
            SaveButtonProps: {
                label: 'Export',
                savingLabel: 'Exporting',
                icon: faDownload,
            },
            initialValues: {
                includeExportedPayroll: true,
                includeProcessedPayroll: false,
            },
            body: ({ values }) => (
                <>
                    <ValidatedSelect
                        placeholder="Select Contract"
                        id="contractId"
                        name="contractId"
                        label="Contract #"
                        options={contracts}
                        required={!values.workOrderNumber}
                        error="A contract or a Work Order Number must be selected"
                    />

                    <Input
                        label="Work Order Number"
                        id="workOrderNumber"
                        name="workOrderNumber"
                        required={!values.contractId}
                        error="A contract or a Work Order Number must be selected"
                    />

                    <DatePicker
                        id="selectedCertifiedPayrollWeekOf"
                        label="Week of"
                        name="selectedCertifiedPayrollWeekOf"
                        required
                    />

                    <FormGroup>
                        <FormCheckbox
                            id="includeInterstitialWeeks"
                            name="includeInterstitialWeeks"
                            labelText="Weeks with no work still count as payroll weeks"
                        />
                    </FormGroup>

                    <FormGroup>
                        <FormLabel
                            label="Included Payroll Statuses"
                            required
                        />

                        <FormCheckbox
                            id="includeExportedPayroll"
                            name="includeExportedPayroll"
                            labelText="Exported"
                            disabled
                            className="mb-1"
                        />

                        <FormCheckbox
                            id="includeProcessedPayroll"
                            name="includeProcessedPayroll"
                            labelText="Processed"
                        />
                    </FormGroup>
                </>
            ),
        });
    };

    onFringeReportExportClicked = async (values) => {
        const {
            selectedMonthOf,
            selectedMultiDispatchLocations,
        } = values;

        if (!selectedMonthOf || !selectedMultiDispatchLocations) return;

        const model = {
            monthOf: new Date(selectedMonthOf),
            dispatchLocationIds: selectedMultiDispatchLocations.map(
                (e) => e.id,
            ),
        };

        try {
            const fileName = `FringeReport_${moment().format(
                'YYYY-M-D_hhmmss',
            )}.xlsx`;

            await httpClient.downloadFile(
                ApiRoutes.payroll.fringeReport(),
                model,
                fileName,
            );

            toasty.success('Downloaded fringe report file successfully');
        } catch (e) {
            toasty.error(`Error exporting fringe report records (${e?.handledError?.message})`);
        }

        this.state.gridOptions.refresh();

        this.setState({
            rowsSelected: [],
        });
    };

    onCertifiedPayrollExportClicked = async (values) => {
        this.setState({ isExporting: true });

        try {
            const {
                contractId,
                workOrderNumber,
                includeInterstitialWeeks,
                selectedCertifiedPayrollWeekOf,
                includeProcessedPayroll,
                includeExportedPayroll,
            } = values;

            const model = {
                contractId,
                workOrderNumber,
                includeInterstitialWeeks,
                includeProcessedPayroll,
                includeExportedPayroll,
                selectedWeekOf: selectedCertifiedPayrollWeekOf,
            };

            await httpClient.downloadServerFile(
                ApiRoutes.payroll.certifiedPayroll(),
                model,
            );

            toasty.success('Downloaded certified payroll file successfully');
        } catch (e) {
            toasty.error(`Error downloading certified payroll records (${e?.handledError?.message})`);
        }

        this.state.gridOptions.refresh();

        this.setState({
            rowsSelected: [],
            isExporting: false,
            showCertifiedPayrollModal: false,
        });
    };

    toggleRecalcModal = () => {
        const { tenant } = this.context;
        const { tenantSettings } = tenant;
        const { payPeriodTypeId } = tenantSettings;

        const { dispatchLocations } = this.state;

        Modal.show({
            title: payPeriodTypeId === PayPeriodTypes.Weekly ? 'Recalculate Week' : 'Recalculate Period',
            onSubmit: (values) => this.onRecalcClick(values),
            SaveButtonProps: {
                label: 'Recalculate',
                savingLabel: 'Recalculating',
                icon: faCalculator,
            },
            initialValues: {
                selectedWeekOf: '',
                selectedDispatchLocation: '',
                employees: [],
                selectedEmployeeIds: '',
            },
            body: ({ setFieldValue, values }) => (
                <>
                    <DatePicker
                        id="selectedWeekOf"
                        label={payPeriodTypeId === PayPeriodTypes.Weekly ? 'Week Of' : 'Period Of'}
                        name="selectedWeekOf"
                        highlightDates={this.getHighlightedPayPeriodDates()}
                        onChange={(e) => {
                            onSelectedPayrollWeekChanged(e.target.value, values.selectedDispatchLocation, setFieldValue);
                        }}
                        required
                        error="You must make a date selection"
                    />

                    <ValidatedSelect
                        placeholder="Select Dispatching"
                        id="selectedDispatchLocation"
                        name="selectedDispatchLocation"
                        label="Dispatching"
                        options={dispatchLocations}
                        onSelectAll={() => {
                            setFieldValue('selectedDispatchLocation', dispatchLocations);
                            onSelectedPayrollDispatchLocationChanged(dispatchLocations, values.selectedWeekOf, setFieldValue);
                        }}
                        isMulti
                        required
                        onChange={(selection) => {
                            onSelectedPayrollDispatchLocationChanged(selection, values.selectedWeekOf, setFieldValue);
                        }}
                        error="A dispatch location must be selected"
                    />

                    <ValidatedSelect
                        placeholder="Select Employee(s)"
                        id="selectedEmployeeIds"
                        name="selectedEmployeeIds"
                        label="Employee(s)"
                        options={values.employees}
                        isLoading={values.loadingEmployees}
                        disabled={isEmpty(values.selectedDispatchLocation)}
                        required
                        isMulti
                        asPrimitive
                        onSelectAll={() => {
                            setFieldValue('selectedEmployeeIds', values.employees?.map((employee) => employee.id));
                        }}
                        error="At least one employee must be selected"
                        formatOptionLabel={(opt, { context }) => {
                            if (context === 'menu') {
                                return (
                                    <FlexColumnStart>
                                        <span>{opt.name}</span>
                                        {Boolean(opt.dispatchCompanyName) && (
                                            <small className="text-muted mt-1">
                                                {opt.dispatchCompanyName}
                                            </small>
                                        )}
                                    </FlexColumnStart>
                                );
                            }

                            return <span>{opt.name}</span>;
                        }}
                    />
                </>
            ),
        });
    };

    toggleRejectPayrollTimesheetModal = () => {
        Modal.show({
            title: 'Reject Payroll Charges',
            onSubmit: (values) => this.rejectPayrollTimesheet(values),
            SaveButtonProps: {
                label: 'Reject',
                savingLabel: 'Rejecting',
                color: 'danger',
                icon: faUndo,
            },
            body: () => (
                <>
                    <div
                        style={{ marginBottom: '20px' }}
                        className="form-text text-danger"
                    >
                        This action will reset all associated
                        approved charges back to pending.
                    </div>

                    <Textarea
                        id="selectedRowRejectionNotes"
                        name="selectedRowRejectionNotes"
                        required
                        placeholder="Enter notes regarding the rejection."
                        error="Notes are required"
                        maxLength="500"
                        rows="5"
                    />
                </>
            ),
        });
    };

    getHighlightedPayPeriodDates = (options = {}) => {
        const { tenant } = this.context;
        const { tenantSettings } = tenant;
        const { payPeriodTypeId, biweeklyPayPeriodStartDate } = tenantSettings;
        const { exportBillableOnly, isHourly } = options;

        if (payPeriodTypeId === PayPeriodTypes.Weekly || exportBillableOnly || isHourly) {
            return [];
        }

        const dates = [
            moment(biweeklyPayPeriodStartDate).startOf('isoWeek').toDate(),
        ];

        while (true) {
            const lastDate = dates[dates.length - 1];

            if (moment(lastDate) < moment()) {
                dates.push(moment(lastDate).add(14, 'day').toDate());
            } else {
                break;
            }
        }

        return dates;
    };

    showFringeModal = () => {
        const { dispatchLocations } = this.state;

        Modal.show({
            title: 'Export Fringe Report',
            onSubmit: (values) => this.onFringeReportExportClicked(values),
            SaveButtonProps: {
                label: 'Export',
                savingLabel: 'Exporting',
                icon: faDownload,
            },
            body: ({ setFieldValue }) => (
                <>
                    <ValidatedSelect
                        placeholder="Select Dispatching"
                        id="selectedMultiDispatchLocations"
                        name="selectedMultiDispatchLocations"
                        label="Dispatching"
                        isClearable
                        isMulti
                        options={dispatchLocations}
                        required
                        error="You must select a dispatch office."
                        onSelectAll={() => {
                            setFieldValue('selectedMultiDispatchLocations', dispatchLocations);
                        }}
                    />

                    <Input
                        label="Month Of"
                        id="monthOf"
                        name="selectedMonthOf"
                        type="date"
                        required
                    />
                </>
            ),
        });
    };

    render() {
        const {
            rowData,
            gridOptions,
            hasEdit,
            hasIndividual,
            rowsSelected,
            canViewBillingReport,
        } = this.state;

        if (this.state.loading || !this.context.tenant.tenantSettings) {
            return (
                <Progress />
            );
        }

        const {
            payrollEnabled, payPeriodTypeId, quirksEnabled, enforceContractUnionWork,
            enablePrevailingWageContracts, payrollAppTypeId,
        } = this.context.tenant.tenantSettings;

        const canShowComplianceReports = Boolean(enforceContractUnionWork) || Boolean(enablePrevailingWageContracts);

        if (!payrollEnabled) return <NotAuthorizedPage />;

        const weeklyPayrollLabel = payPeriodTypeId === PayPeriodTypes.Weekly
            ? 'Export Weekly - Payroll'
            : 'Export Biweekly - Payroll';

        return (
            <PageWrap>
                <PageHeading>
                    <FlexBetweenRow>
                        <div>
                            <FontAwesomeIcon
                                icon={faMoneyCheck}
                                className="mr-2 text-muted"
                            />

                            <span>Payroll: Pay Categories</span>
                        </div>

                        <div>
                            {hasEdit && (
                                <>
                                    <Button
                                        size="sm"
                                        color="success"
                                        onClick={() => this.toggleRecalcModal()}
                                    >
                                        <FontAwesomeIcon
                                            icon={faCalculator}
                                            className="mr-2"
                                        />

                                        {payPeriodTypeId === PayPeriodTypes.Weekly ? 'Recalculate Week' : 'Recalculate Period'}
                                    </Button>

                                    <Button
                                        size="sm"
                                        color="danger"
                                        className="ml-2"
                                        onClick={() => this.toggleRejectPayrollTimesheetModal()}
                                        disabled={rowsSelected.length !== 1}
                                    >
                                        Reject To Payroll Charges
                                    </Button>
                                </>
                            )}

                            <Can
                                do="export"
                                on="payroll_review"
                            >
                                <>
                                    {(!quirksEnabled || payrollAppTypeId === PayrollAppTypes.ADP) && (
                                        <UncontrolledButtonDropdown>
                                            <Button
                                                className="ml-2"
                                                size="sm"
                                                color="warning"
                                                onClick={() => this.toggleExportModal(false, false, false, true)}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faDownload}
                                                    className="mr-2"
                                                />
                                                {`Export to ${getPayrollAppLabel(payrollAppTypeId)}`}
                                            </Button>

                                            <DropdownToggle
                                                split
                                                color="warning"
                                            />

                                            <DropdownMenu>
                                                <DropdownItem
                                                    onClick={() => this.toggleExportModal(false, false, false, true, true)}
                                                >
                                                    Download Preview Export
                                                </DropdownItem>

                                                {payrollAppTypeId === PayrollAppTypes.ADP && (
                                                    <DropdownItem
                                                        onClick={() => this.toggleExportModal(false, false, false, true, true, true)}
                                                    >
                                                        Download Legacy Preview Export
                                                    </DropdownItem>
                                                )}
                                            </DropdownMenu>
                                        </UncontrolledButtonDropdown>
                                    )}
                                </>
                            </Can>

                            <Can
                                do="export"
                                on="payroll_review"
                            >
                                {canShowComplianceReports && (
                                    <Button
                                        className="ml-2"
                                        size="sm"
                                        color="secondary"
                                        onClick={this.showFringeModal}
                                    >
                                        Fringe Report
                                    </Button>
                                )}
                            </Can>

                            <Can
                                do="export_certified_payroll"
                                on="payroll_review"
                            >
                                {canShowComplianceReports && (
                                    <Button
                                        className="ml-2"
                                        size="sm"
                                        color="primary"
                                        onClick={this.toggleCertifiedPayrollModal}
                                    >
                                        Certified Payroll
                                    </Button>
                                )}
                            </Can>
                        </div>
                    </FlexBetweenRow>
                </PageHeading>

                <DataGridToolbar
                    entity="Payroll"
                    gridApi={this.state.gridApi}
                    dataSource={this.state.dataSource}
                    hideAdd
                    hideExcelButton
                    gridOptions={this.state.gridOptions}
                    serverExport={{
                        apiPath: getExcelAPIPath(hasEdit),
                        filePrefix: 'PayrollCategoriesSearch',
                    }}
                >
                    <Can
                        do="export"
                        on="payroll_review"
                    >
                        {!hasIndividual && (
                            <SmallButton
                                onClick={() => this.toggleExportModal(
                                    false,
                                    false,
                                    true,
                                )}
                            >
                                <i className="fa fa-file-excel fa-lg" />
                                <span className="ml-2 small-viewport-hide">
                                    Hours Report
                                </span>
                            </SmallButton>
                        )}
                        {!hasIndividual && (
                            <SmallButton
                                onClick={() => this.toggleExportModal(
                                    true,
                                    false,
                                    false,
                                )}
                            >
                                <i className="fa fa-file-excel fa-lg" />
                                <span className="ml-2 small-viewport-hide">
                                    {weeklyPayrollLabel}
                                </span>
                            </SmallButton>
                        )}
                        {canViewBillingReport && (
                            <SmallButton
                                onClick={() => this.toggleExportModal(
                                    true,
                                    true,
                                    false,
                                )}
                            >
                                <i className="fa fa-file-excel fa-lg" />
                                <span className="ml-2 small-viewport-hide">
                                    Export Weekly - Billing
                                </span>
                            </SmallButton>
                        )}
                    </Can>
                </DataGridToolbar>

                <DataGrid
                    domLayout="normal"
                    rowData={rowData}
                    gridOptions={gridOptions}
                    gridStatus={this.state.gridStatus}
                />

                <TimesheetDetails
                    ref={this.timesheetDetailsRef}
                    show={this.state.timesheetDetailsOpen}
                    toggleShow={(open) => this.setState({
                        timesheetDetailsOpen: open,
                    })}
                    onClose={this.onTimesheetDetailsClosed}
                    payroll
                    isPayroll
                    isBilling={false}
                    isTimesheet // janky way to show just reaodnly part of the status of the emergency
                    isTimesheetForm
                />

                <PayrollCalculationSlideout
                    ref={this.payrollCalculationRef}
                    show={this.state.payrollCalculationOpen}
                    toggleShow={(open) => this.setState({
                        payrollCalculationOpen: open,
                    })}
                    onClose={this.onPayrollCalculationClosed}
                />
            </PageWrap>
        );
    }
}

export default PayrollIndex;
